// ============================================
// Settings - Chat Modal Module (ES6)
// 채팅 설정 모달
// ============================================

import { showToast, showGlobalLoading, hideGlobalLoading } from '../utils.js';
import { getCurrentRoomId, getCurrentSessionId, getCurrentChatData, isProcessing, setCurrentRoomId, setCurrentSessionId } from '../app/state.js';
import { deleteSession } from '../chat/actions.js';
import { showConfirm } from '../shared/confirm-modal.js';
import { authFetch } from '../shared/auth-fetch.js';
import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { loadSession } from '../chat/load.js';
import { invalidateChatCache, loadChatList } from '../chat/list.js';

// ============================================
// Chat Settings Modal
// ============================================

export function openChatSettingsModal() {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  if (!roomId || !sessionId) {
    showToast('채팅을 먼저 선택해주세요.', 'error');
    return;
  }

  // 스트리밍 중 설정 변경 차단
  if (isProcessing()) {
    showToast('메세지 대기중에는 설정을 변경할 수 없습니다.', 'error');
    return;
  }

  const modal = document.createElement('div');
  modal.id = 'chatSettingsModal';
  modal.className = 'settings-full-modal';

  // 정적 HTML - XSS 위험 없음
  const headerHtml = `
    <div class="settings-full-header">
      <button class="settings-back-btn" data-action="close">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>
      <h2>상담 설정</h2>
      <div style="width: 36px;"></div>
    </div>
  `;

  const contentHtml = `
    <div class="settings-full-content">
      <div class="settings-empty-placeholder">
        <div style="color: var(--text-tertiary); text-align: center; padding: 60px 20px;">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="opacity: 0.4; margin-bottom: 12px;">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          <div style="font-size: 14px;">추가 설정 준비 중</div>
        </div>
      </div>
    </div>
  `;

  modal.innerHTML = headerHtml + contentHtml;

  // 이벤트 위임 설정
  modal.addEventListener('click', (e) => {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    if (action === 'close') {
      closeChatSettingsModal();
    }
  });

  document.body.appendChild(modal);

  requestAnimationFrame(() => {
    modal.classList.add('open');
    if (window.ModalManager) {
      window.ModalManager.register('chatSettingsModal', closeChatSettingsModal, 'fullscreen');
      window.ModalManager.push('chatSettingsModal');
    }
  });
}

export function closeChatSettingsModal() {
  const modal = document.getElementById('chatSettingsModal');
  if (!modal) return;

  modal.classList.remove('open');

  setTimeout(() => {
    modal.remove();
    if (window.ModalManager) {
      window.ModalManager.unregister('chatSettingsModal');
    }
  }, 300);

  if (window.ModalManager) {
    window.ModalManager.remove('chatSettingsModal', true);
  }
}

export function saveAndCloseSettings() {
  closeChatSettingsModal();
}

// ============================================
// Chat Actions
// ============================================

export async function restartChatFromSettings() {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  const currentChatData = getCurrentChatData();
  if (!roomId || !sessionId || !currentChatData) return;

  const contentId = currentChatData.publishedId;
  if (!contentId) {
    showToast('원본 콘텐츠를 찾을 수 없습니다.', 'error');
    return;
  }

  const confirmed = await showConfirm({
    message: '새 상담이 생성됩니다.\n현재 채팅 내용은 사라지지 않아요.',
    confirmText: '새 상담 시작'
  });
  if (!confirmed) return;

  closeChatSettingsModal();
  showGlobalLoading();

  try {
    const response = await authFetch(CHAT_ENDPOINTS.startPublishedChat, {
      body: { contentId }
    });

    const data = await response.json();

    if (!response.ok) {
      showToast(data.error || '새 상담 생성 실패', 'error');
      hideGlobalLoading();
      return;
    }

    // 새 세션으로 전환
    setCurrentRoomId(data.roomId);
    setCurrentSessionId(data.sessionId);
    hideGlobalLoading();
    invalidateChatCache();
    loadChatList(0, true);
    await loadSession(data.roomId, data.sessionId);
    showToast(`"${data.sessionName}" 상담이 시작되었습니다`, 'success');
  } catch (e) {
    hideGlobalLoading();
    showToast('새 상담 생성 실패: ' + e.message, 'error');
    console.error('[ChatModal] 새로하기 실패:', e);
  }
}

export async function duplicateSessionFromSettings() {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  const currentChatData = getCurrentChatData();
  if (!roomId || !sessionId) return;

  const sessionName = currentChatData?.sessionName || '현재 상담';

  const confirmed = await showConfirm({
    message: `현재 상담을 복제합니다.\n복제된 상담으로 이동합니다.`,
    confirmText: '복제하기'
  });
  if (!confirmed) return;

  closeChatSettingsModal();
  showGlobalLoading();

  try {
    const response = await authFetch(CHAT_ENDPOINTS.duplicateSession, {
      body: { roomId, sessionId }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '복제 실패');
    }

    const result = await response.json();

    // 복제된 세션으로 이동
    setCurrentRoomId(result.roomId);
    setCurrentSessionId(result.sessionId);
    invalidateChatCache();
    loadChatList(0, true);
    await loadSession(result.roomId, result.sessionId);
    showToast(`"${result.sessionName}" 상담이 복제되었습니다`, 'success');
  } catch (e) {
    showToast('상담 복제 실패: ' + e.message, 'error');
    console.error('[ChatModal] 복제 실패:', e);
  } finally {
    hideGlobalLoading();
  }
}

export async function deleteSessionFromSettings() {
  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();
  if (!roomId || !sessionId) return;

  const confirmed2 = await showConfirm({
    message: '이 상담을 삭제할까요?\n삭제된 상담은 복구할 수 없습니다.',
    confirmText: '삭제',
    isDanger: true
  });
  if (!confirmed2) return;

  closeChatSettingsModal();

  await deleteSession(roomId, sessionId, true);
}

// ============================================
// Settings Modal (App Settings)
// ============================================

export function closeSettingsModal() {
  const modal = document.getElementById('settingsModal');
  if (modal) modal.classList.remove('open');
  if (window.ModalManager) {
    window.ModalManager.remove('settingsModal', true);
  }
}
